import type { CSSProperties, FC, ReactNode } from 'react'
import { memo, useMemo } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { alpha, Box } from '@mui/material'
import { Highlight, themes } from 'prism-react-renderer'
import { CopyButton } from '@/common/components'
import { Prism } from '../utils/prism-languages'
import { getLanguageFromMarkdownFence } from '../utils/fileModificationUtils'

interface CodeBlockProps {
  readonly children: ReactNode
  readonly theme: Theme
  readonly language?: string | undefined
}

export const CodeBlock: FC<CodeBlockProps> = memo(({ children, theme, language }) => {
  const codeString = useMemo(() => extractTextContent(children), [children])
  const prismLanguage = getLanguageFromMarkdownFence(language)

  if (prismLanguage && prismLanguage !== 'text') {
    return (
      <Box sx={containerSx}>
        <Box className="code-copy-button" sx={copyButtonWrapperSx}>
          <CopyButton
            text={codeString}
            variant="overlay"
            tooltipText="Copy code"
            sx={copyButtonPositionSx(theme)}
          />
        </Box>

        <Highlight
          theme={theme.palette.mode === 'dark' ? themes.vsDark : themes.vsLight}
          code={codeString}
          language={prismLanguage}
          prism={Prism}
        >
          {({ style, tokens, getLineProps, getTokenProps }) => (
            <Box component="pre" sx={preElementSxWithHighlight(theme, style)}>
              {tokens.map((line, lineIndex) => {
                const lineProps = getLineProps({ line })
                const lineContent = line.map((t) => t.content).join('')
                const lineKey = `${lineIndex}-${lineContent.slice(0, 20)}`
                return (
                  <div
                    key={lineKey}
                    {...lineProps}
                    style={{
                      ...lineProps.style,
                      display: 'block',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {line.map((token, tokenIndex) => {
                      const tokenProps = getTokenProps({ token })
                      const tokenKey = `${lineIndex}-${tokenIndex}-${token.content.slice(0, 10)}`
                      return <span key={tokenKey} {...tokenProps} />
                    })}
                  </div>
                )
              })}
            </Box>
          )}
        </Highlight>
      </Box>
    )
  }

  return (
    <Box sx={containerSx}>
      <Box className="code-copy-button" sx={copyButtonWrapperSx}>
        <CopyButton
          text={codeString}
          variant="overlay"
          tooltipText="Copy code"
          sx={copyButtonPositionSx(theme)}
        />
      </Box>

      <Box component="pre" sx={preElementSx(theme)}>
        <Box component="code" sx={codeElementSx(theme)}>
          {children}
        </Box>
      </Box>
    </Box>
  )
})

CodeBlock.displayName = 'CodeBlock'

function extractTextContent(node: ReactNode): string {
  if (typeof node === 'string') return node
  if (typeof node === 'number') return String(node)
  if (Array.isArray(node)) {
    return node.map(extractTextContent).join('')
  }
  return ''
}

const containerSx: SxProps<Theme> = {
  my: 2,
  position: 'relative',
  '&:hover .code-copy-button': {
    opacity: 1,
  },
}

const copyButtonWrapperSx: SxProps<Theme> = {
  opacity: 0,
  transition: 'opacity 0.2s ease-in-out',
}

const copyButtonPositionSx = (theme: Theme): SxProps<Theme> => ({
  position: 'absolute',
  bottom: theme.spacing(1),
  right: theme.spacing(1),
  zIndex: 1,
})

const preElementSx = (theme: Theme): SxProps<Theme> => {
  const isDark = theme.palette.mode === 'dark'
  const overlayColor = isDark ? theme.palette.common.white : '#3E2723'
  return {
    margin: 0,
    borderRadius: '2px',
    border: `1px solid ${alpha(overlayColor, isDark ? 0.1 : 0.08)}`,
    boxShadow: `0 2px 8px ${alpha(isDark ? theme.palette.common.black : '#3E2723', isDark ? 0.3 : 0.08)}`,
    backgroundColor: alpha(overlayColor, isDark ? 0.03 : 0.02),
    overflow: 'auto',
    overflowX: 'auto',
    p: 2,
    '&::-webkit-scrollbar': {
      width: '8px',
      height: '8px',
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: alpha(overlayColor, isDark ? 0.05 : 0.03),
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: alpha(overlayColor, isDark ? 0.2 : 0.15),
      borderRadius: '4px',
      '&:hover': {
        backgroundColor: alpha(overlayColor, isDark ? 0.3 : 0.25),
      },
    },
  }
}

const codeElementSx = (theme: Theme): SxProps<Theme> => ({
  fontFamily: '"Fira Code", "JetBrains Mono", "SF Mono", Monaco, monospace',
  fontSize: '0.875em',
  lineHeight: 1.5,
  color: theme.palette.text.primary,
  whiteSpace: 'pre',
})

const preElementSxWithHighlight = (theme: Theme, style: CSSProperties): SxProps<Theme> => {
  const isDark = theme.palette.mode === 'dark'
  const overlayColor = isDark ? theme.palette.common.white : '#3E2723'
  return {
    ...(style as Record<string, unknown>),
    margin: 0,
    borderRadius: '2px',
    border: `1px solid ${alpha(overlayColor, isDark ? 0.1 : 0.08)}`,
    boxShadow: `0 2px 8px ${alpha(isDark ? theme.palette.common.black : '#3E2723', isDark ? 0.3 : 0.08)}`,
    backgroundColor: 'transparent',
    overflow: 'auto',
    overflowX: 'auto',
    p: 2,
    fontFamily: '"Fira Code", "JetBrains Mono", "SF Mono", Monaco, monospace',
    fontSize: '0.875em',
    lineHeight: 1.5,
    '&::-webkit-scrollbar': {
      width: '8px',
      height: '8px',
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: alpha(overlayColor, isDark ? 0.05 : 0.03),
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: alpha(overlayColor, isDark ? 0.2 : 0.15),
      borderRadius: '4px',
      '&:hover': {
        backgroundColor: alpha(overlayColor, isDark ? 0.3 : 0.25),
      },
    },
  }
}
